import {PlatformPressable, useHeaderHeight} from '@react-navigation/elements';
import type {StackScreenProps} from '@react-navigation/stack';
import chroma from 'chroma-js';
import {parsePhoneNumberWithError} from 'libphonenumber-js/max';
import {observer} from 'mobx-react-lite';
import {useCallback, useLayoutEffect, useMemo} from 'react';
import {Linking, StyleSheet, View} from 'react-native';

import BookmarkFill from '../../../assets/bookmark_fill.svg';
import BookmarkOutline from '../../../assets/bookmark_outline.svg';
import {useRoot} from '../../Root';
import type {RootStackParamList} from '../../RootStack/RootStackParamList';
import ListGroupsScreen from '../../screens/ListGroupsScreen/ListGroupsScreen';
import {useStyles} from '../../styling';
import {exportSupportGroupSchedule} from '../../SupportGroupScheduleExport';
import useCityGroups from './useCityGroups';

export type ListGroupsBindingProps = StackScreenProps<
  RootStackParamList,
  'ListGroups'
>;

export default observer(function ListGroupsBinding(
  props: ListGroupsBindingProps,
) {
  const {route, navigation} = props;
  const {cityId: _cityId} = route.params ?? {};
  const cityId = parseInt(_cityId ?? '');

  const onGroupPress = useCallback(
    (id: number) => {
      navigation.navigate('ShowGroup', {id: `${id}`});
    },
    [navigation],
  );

  const root = useRoot();

  const now = root.currentTime.raw;

  const {city, groups, localTime} = useCityGroups({
    now,
    cityId,
  });

  const onGroupNotificationsPress = useCallback(
    async (id: number) => {
      const group = groups?.find(_ => _.id === id);
      if (!city || !group) {
        return;
      }
      await exportSupportGroupSchedule(root, now, city, group);
    },
    [city, groups, now, root],
  );

  const openTelLink = useCallback(async () => {
    const {phoneNumber} = city ?? {};
    if (!phoneNumber) {
      return;
    }
    try {
      const parsed = parsePhoneNumberWithError(phoneNumber, 'RU');
      await Linking.openURL(parsed.getURI());
    } catch {
      /* empty */
    }
  }, [city]);

  const headerHeight = useHeaderHeight();

  const _phoneNumber = city?.phoneNumber;
  const phoneNumber = useMemo(
    () => formatPhoneNumber(_phoneNumber),
    [_phoneNumber],
  );

  const styles = useStyles(theme => ({
    headerTitle: {
      ...theme.text('header5'),
      color: theme.palette.textPrimary,
      fontSize: 20,
      lineHeight: 35,
      textShadowColor: theme.palette.background,
      textShadowOffset: {height: 1, width: 0},
      textShadowRadius: 8,
    },
    headerBackgroundContainer: {
      backgroundColor: chroma(theme.palette.background).alpha(0.8).hex('rgba'),
    },
  }));

  const favoritesRecord = root.favoritesStorage.current;
  const favoriteSet = useMemo(
    () =>
      favoritesRecord
        ? new Set(favoritesRecord.cities.map(_ => _.id))
        : undefined,
    [favoritesRecord],
  );
  const isFavorite = favoriteSet?.has(cityId) ?? false;
  const toggleFavorites = useCallback(() => {
    root.favoritesStorage.set(record => {
      if (!record) {
        return {cities: [{id: cityId}]};
      }
      const next = record.cities.filter(_ => _.id !== cityId);
      if (next.length === record.cities.length) {
        return {cities: [...next, {id: cityId}]};
      }
      return {cities: next};
    });
  }, [cityId, root]);

  useLayoutEffect(() => {
    if (!city) {
      return;
    }
    navigation.setOptions({
      headerTitle: city.name,
      headerShown: true,
      headerTitleStyle: styles.headerTitle,
      headerTitleAlign: 'center',
      headerBackgroundContainerStyle: styles.headerBackgroundContainer,
      headerRight: _props => {
        const {pressColor, pressOpacity, tintColor} = _props;
        return (
          <PlatformPressable
            onPress={toggleFavorites}
            pressColor={pressColor}
            pressOpacity={pressOpacity}>
            <View style={layoutStyles.headerRightIcon}>
              {isFavorite ? (
                <BookmarkFill color={tintColor} />
              ) : (
                <BookmarkOutline color={tintColor} />
              )}
            </View>
          </PlatformPressable>
        );
      },
    });
  }, [city, isFavorite, navigation, styles, toggleFavorites]);

  return (
    <ListGroupsScreen
      phoneNumber={phoneNumber}
      phoneNumberDescription={city?.phoneNumberDescription}
      onPhoneNumberPress={openTelLink}
      groups={groups}
      onGroupPress={onGroupPress}
      onGroupNotificationsPress={onGroupNotificationsPress}
      localTime={localTime}
      compensateHeaderHeight={headerHeight}
    />
  );
});

const layoutStyles = StyleSheet.create({
  headerRightIcon: {
    marginHorizontal: 24,
  },
});

function formatPhoneNumber(_: string | undefined | null) {
  if (typeof _ !== 'string') {
    return _;
  }
  try {
    const parsed = parsePhoneNumberWithError(_, 'RU');
    return parsed.formatNational();
  } catch {
    return '';
  }
}
